import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useId, useLayoutEffect, useRef } from 'preact/hooks';

// ============================================================ avisos (toasts)

type Aviso = { id: number; texto: string; tipo: 'ok' | 'erro' };
const avisos = signal<Aviso[]>([]);
let proximoAviso = 1;

export function avisar(texto: string, tipo: 'ok' | 'erro' = 'ok') {
  const id = proximoAviso++;
  // No máximo 3 avisos na tela: uma rajada de ações não pode cobrir o conteúdo.
  avisos.value = [...avisos.value, { id, texto, tipo }].slice(-3);
  setTimeout(() => dispensar(id), tipo === 'erro' ? 9000 : 4500);
}

const dispensar = (id: number) => (avisos.value = avisos.value.filter((a) => a.id !== id));

/** Região "ao vivo": leitores de tela anunciam os avisos sem tirar o foco de onde a pessoa está. */
export function Avisos() {
  return (
    <div class="avisos" role="status" aria-live="polite">
      {avisos.value.map((a) => (
        <div key={a.id} class={`aviso-toast ${a.tipo}`} onClick={() => dispensar(a.id)} title="Clique para fechar">{a.texto}</div>
      ))}
    </div>
  );
}

// ============================================================ confirmação

type Pedido = { texto: string; confirmar: string; perigo: boolean; resolver: (ok: boolean) => void };
const pedido = signal<Pedido | null>(null);

/** Pergunta antes de uma ação destrutiva. Retorna true se a pessoa confirmou. */
export function confirmar(texto: string, opcoes: { confirmar?: string; perigo?: boolean } = {}): Promise<boolean> {
  return new Promise((resolver) => {
    pedido.value = { texto, confirmar: opcoes.confirmar ?? 'Confirmar', perigo: opcoes.perigo ?? false, resolver };
  });
}

export function DialogoConfirmar() {
  const p = pedido.value;
  if (!p) return null;
  const fechar = (ok: boolean) => {
    pedido.value = null;
    p.resolver(ok);
  };
  return (
    <Modal titulo="Confirmar" onFechar={() => fechar(false)} pequeno>
      <p>{p.texto}</p>
      <div class="modal-acoes">
        <button type="button" class="botao" onClick={() => fechar(false)}>Cancelar</button>
        <button type="button" class={p.perigo ? 'botao perigo' : 'botao primario'} onClick={() => fechar(true)} autofocus>
          {p.confirmar}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================ modal

const FOCAVEL = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ModalProps = {
  titulo: string;
  onFechar: () => void;
  children: ComponentChildren;
  /** Janela estreita (confirmações). */
  pequeno?: boolean;
  /** Janela larga (tabelas e formulários grandes). */
  largo?: boolean;
};

/**
 * Janela modal acessível: foco entra ao abrir e volta para quem abriu ao fechar, Tab não escapa da janela,
 * Esc fecha, e clicar fora fecha. Vários modais empilhados funcionam (o de cima trata o Esc).
 */
export function Modal({ titulo, onFechar, children, pequeno, largo }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const tituloId = useId();

  // useLayoutEffect (e não useEffect): atalhos e foco precisam valer assim que a janela aparece, sem esperar a pintura.
  useLayoutEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const primeiro = el?.querySelector<HTMLElement>('[autofocus], input, select, textarea') ?? el?.querySelector<HTMLElement>(FOCAVEL);
    (primeiro ?? el)?.focus();

    const aoTeclar = (e: KeyboardEvent) => {
      if (!el) return;
      // Só o modal mais ao topo responde.
      const todos = document.querySelectorAll('.modal');
      if (todos[todos.length - 1] !== el) return;
      if (e.key === 'Escape') {
        // Calendário aberto dentro da janela: o Esc fecha só o calendário (ele mesmo trata a tecla).
        if (document.querySelector('.cal-pop')) return;
        e.stopPropagation();
        onFechar();
      } else if (e.key === 'Tab') {
        const itens = [...el.querySelectorAll<HTMLElement>(FOCAVEL)].filter((x) => x.offsetParent !== null);
        if (itens.length === 0) return;
        const a = itens[0]!;
        const z = itens[itens.length - 1]!;
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener('keydown', aoTeclar, true);
    return () => {
      document.removeEventListener('keydown', aoTeclar, true);
      anterior?.focus?.();
    };
  }, []);

  return (
    <div class="modal-fundo" onMouseDown={(e) => e.target === e.currentTarget && onFechar()}>
      <div class={`modal${pequeno ? ' pequeno' : ''}${largo ? ' largo' : ''}`} role="dialog" aria-modal="true" aria-labelledby={tituloId} ref={ref} tabIndex={-1}>
        <header class="modal-topo">
          <h2 id={tituloId}>{titulo}</h2>
          <button type="button" class="fechar" onClick={onFechar} aria-label="Fechar">✕</button>
        </header>
        <div class="modal-corpo">{children}</div>
      </div>
    </div>
  );
}

// ============================================================ formulário

/** Rótulo + controle + mensagem de erro/ajuda, ligados por id (leitor de tela lê junto). */
export function Campo({ rotulo, dica, erro, children, larg }: { rotulo: string; dica?: string; erro?: string; children: (id: string) => ComponentChildren; larg?: 'meio' | 'inteiro' }) {
  const id = useId();
  return (
    <div class={`campo-form${larg === 'meio' ? ' meio' : ''}`}>
      <label for={id}>{rotulo}</label>
      {children(id)}
      {dica && !erro && <small>{dica}</small>}
      {erro && <small class="erro" role="alert">{erro}</small>}
    </div>
  );
}

export function ErroForm({ texto }: { texto: string }) {
  return texto ? <p class="erro caixa-erro" role="alert">{texto}</p> : null;
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return <p class="vazio" aria-busy="true">{texto}</p>;
}

export function Selo({ texto, tom }: { texto: string; tom?: 'verde' | 'azul' | 'ambar' | 'vermelho' | 'cinza' }) {
  return <span class={`selo selo-tom-${tom ?? 'cinza'}`}>{texto}</span>;
}

export function Barra({ pct, cor }: { pct: number; cor?: string }) {
  return (
    <div class="trilho" role="img" aria-label={`${pct}%`}>
      <div class="preench" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, ...(cor ? { background: cor } : {}) }} />
    </div>
  );
}
